import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'

// action
import {
	routeActions,
	configActions,
	adminActions,
	accessControlActions,
	adminUsersActions,
	shopActions,
	productActions,
	seasonActions,
	couponActions,
	paymentActions,
	orderActions,
	customerActions,
	inquiryActions,
	customerOrderActions,
	inquiryMgmtActions,
} from '@redux/store/index'

// lib
import { Logger } from '@utils'

// type
import type { ConfigStateType, TabType, MenuType, CrudType } from '@redux/types'

interface MatchMenuResultType {
	isIncluded: boolean
	selectedMenu: MenuType
}

interface MergeTabListResultType {
	isChanged: boolean
	tabList: TabType[]
}

const matchPathWithMenu = (menu: MenuType[], path: string): MatchMenuResultType => {
	for (const aMenu of menu) {
		if (aMenu.children !== undefined) {
			const res: MatchMenuResultType = matchPathWithMenu(aMenu.children, path)

			if (res.isIncluded) {
				return res
			}
		} else if (aMenu.to === path) {
			return { isIncluded: true, selectedMenu: aMenu }
		}
	}

	return { isIncluded: false, selectedMenu: { id: '', name: '' } }
}

const getMode = (currentPath: string): ConfigStateType['mode'] => {
	const pathParts = currentPath.replace('/', '').split('/')
	const result: ConfigStateType['mode'] = pathParts[0] === 'admin' ? 'admin' : 'main'

	return result
}

const mergeTabList = (tabList: TabType[], newTab: TabType): MergeTabListResultType => {
	const duplicatedTab: TabType[] = tabList.filter(
		(aTab: TabType) => aTab.id === newTab.id,
	)

	const newTabList: TabType[] =
		duplicatedTab.length === 0 ? [...tabList, newTab] : [...tabList]

	return {
		isChanged: newTab !== undefined && duplicatedTab.length === 0,
		tabList: [...newTabList],
	}
}

const convertMenuToTab = (aMenu: MenuType): TabType => {
	return {
		id: aMenu.id,
		name: aMenu.name,
		path: aMenu.to ?? '',
		i18n: aMenu.i18n ?? [],
	}
}

const getSelectedPath = (currentPath: string, tabList: TabType[]) => {
	const idx: number = tabList.findIndex((aTab: TabType) => aTab.path === currentPath)
	const result: string = idx >= 0 ? (idx + 1).toString() : ''

	return result
}

function* currentPathSaga() {
	try {
		const currentPath: string = yield select(state => state.route.currentPath)
		const mode: ConfigStateType['mode'] = yield select(state => state.config.mode)
		const menu: MenuType[] = yield select(state => state.main.menu)
		const adminMenu: MenuType[] = yield select(state => state.admin.menu)
		const configTabList: TabType[] = yield select(state => state.config.tabList)
		const isLoading: boolean = yield select(state => state.config.isLoading)
		const currentAccess: CrudType = yield select(state => state.auth.currentAccess)

		if (currentAccess.indexOf('R') > -1) {
			const matchedMenu: MatchMenuResultType = yield call(
				matchPathWithMenu,
				[...menu],
				currentPath,
			)
			const matchedAdminMenu: MatchMenuResultType = yield call(
				matchPathWithMenu,
				[...adminMenu],
				currentPath,
			)

			const newMode: ConfigStateType['mode'] = yield call(getMode, currentPath)

			if (mode !== newMode) {
				yield put(configActions.MODE(newMode))
			}

			if (matchedMenu.isIncluded || matchedAdminMenu.isIncluded) {
				const newMainTab: TabType = convertMenuToTab(matchedMenu.selectedMenu)
				const newAdminMainTab: TabType = convertMenuToTab(matchedAdminMenu.selectedMenu)

				const newConfigTabList: MergeTabListResultType = mergeTabList(
					configTabList,
					matchedMenu.isIncluded ? newMainTab : newAdminMainTab,
				)

				if (newConfigTabList.isChanged) {
					yield put(configActions.TAB_LIST([...newConfigTabList.tabList]))
				}

				const configIdx: string = getSelectedPath(currentPath, newConfigTabList.tabList)

				yield put(configActions.SELECTED_TAB_IDX(configIdx))
			}
		}

		// release loading
		if (isLoading) {
			yield delay(500)
			yield put(configActions.IS_LOADING(false))
		}

		// screen callback
		if (currentPath === '/admin/menu') {
			yield put(adminActions.TRIGGER_ADMIN_MENU_PAGE(true))
		} else if (currentPath === '/admin/create-menu') {
			yield put(adminActions.TRIGGER_CREATE_MENU_PAGE(true))
		} else if (currentPath === '/admin/access-control') {
			yield put(accessControlActions.TRIGGER_INIT_ACCESS_CONTROL(true))
		} else if (currentPath === '/admin/users') {
			yield put(adminUsersActions.TRIGGER_INIT_ADMIN_USERS(true))
		} else if (currentPath === '/shop') {
			yield put(shopActions.TRIGGER_INIT_SHOP_PAGE(true))
		} else if (currentPath.startsWith('/shop')) {
			const productCode = currentPath.replace('/shop/', '')
			yield put(shopActions.TRIGGER_INIT_SHOP_ITEM_PAGE(true))
			yield put(shopActions.GET_SHOP_ITEM_OPTION(productCode))
		} else if (currentPath === '/cart') {
			yield put(shopActions.TRIGGER_INIT_SHOP_PAGE(true))
		} else if (currentPath === '/main/product') {
			yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE(true))
		} else if (currentPath === '/main/item') {
			yield put(productActions.TRIGGER_INIT_PRODUCT_PAGE(true))
		} else if (currentPath === '/main/season') {
			yield put(seasonActions.TRIGGER_INIT_SEASON_PAGE(true))
		} else if (currentPath === '/main/order') {
			yield put(orderActions.TRIGGER_INIT_ORDER_PAGE(true))
		} else if (currentPath === '/main/customer') {
			yield put(customerActions.TRIGGER_INIT_CUSTOMER_PAGE(true))
		} else if (currentPath === '/main/coupon') {
			yield put(couponActions.TRIGGER_INIT_COUPON_PAGE(true))
		} else if (currentPath === '/payment') {
			yield put(paymentActions.TRIGGER_INIT_PAYMENT_PAGE(true))
		} else if (currentPath === '/contact') {
			yield put(inquiryActions.TRIGGER_INIT_INQUIRY_PAGE(true))
		} else if (currentPath === '/customer/order') {
			yield put(customerOrderActions.TRIGGER_INIT_CUSTOMER_ORDER_PAGE(true))
		} else if (currentPath === '/main/inquiry') {
			yield put(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		}

		window.scrollTo(0, 0)
	} catch (error) {
		Logger.warn(error)
	}
}

function* removeTabSaga() {
	try {
		const currentPath: string = yield select(state => state.route.currentPath)
		const removeTabIdx: number = yield select(state => state.route.removeTabIdx)
		const selectedTabIdx: string = yield select(state => state.config.selectedTabIdx)
		const tabList: TabType[] = yield select(state => state.config.tabList)
		const mode: ConfigStateType['mode'] = yield select(state => state.config.mode)

		const currentSelectedTabIdx: number = parseInt(selectedTabIdx) - 1
		const newTabs: TabType[] = Array.from([...tabList])
		newTabs.splice(removeTabIdx, 1)

		yield put(configActions.TAB_LIST([...newTabs]))

		if (newTabs.length > 0) {
			if (currentSelectedTabIdx === removeTabIdx) {
				const newSelectedTabIdx: string =
					removeTabIdx === 0 ? selectedTabIdx : removeTabIdx.toString()

				const newPath: string =
					removeTabIdx === 0 ? newTabs[0].path : newTabs[removeTabIdx - 1].path

				yield put(configActions.SELECTED_TAB_IDX(newSelectedTabIdx))
				yield put(routeActions.NAVIGATE_PATH(newPath))
			} else if (currentSelectedTabIdx > removeTabIdx) {
				yield put(routeActions.NAVIGATE_PATH(currentPath))
			}
		} else {
			const emptyPath: string = mode === 'admin' ? '/admin/home' : '/main/home'
			yield put(routeActions.NAVIGATE_PATH(emptyPath))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	// path
	yield takeEvery(routeActions.CURRENT_PATH, currentPathSaga)
	yield takeEvery(routeActions.REMOVE_TAB_IDX, removeTabSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
